// Based on useHooks.com's useWindowSize implementation,
//   modified so that it is throttled
// https://usehooks.com/useWindowSize/

import { useEffect, useMemo, useState } from 'react';
import throttle from 'lodash/throttle';

// Define general type for useWindowSize hook, which includes width and height
interface Size {
  width: number | undefined;
  height: number | undefined;
}

// Hook
function useWindowSize(): Size {
  // Initialize state with undefined width/height so server and client renders match
  // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
  const [windowSize, setWindowSize] = useState<Size>({
    width: undefined,
    height: undefined,
  });
  useEffect(() => {
    // Handler to call on window resize
    const handleResize = throttle(() => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }, 500);

    // Add event listener
    window.addEventListener('resize', handleResize);
    // Call handler right away so state gets updated with initial window size
    handleResize();
    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty array ensures that effect is only run on mount
  return windowSize;
}
export default useWindowSize;

export const useIsSmallerWindowWidth = ({
  size,
  maxWidth,
}: {
  size: Size;
  maxWidth: number;
}): boolean => {
  const isSmaller = useMemo(() => size.width && size.width < maxWidth, [size]);
  return isSmaller || false;
};

export const useIsMobileSize = (): boolean => {
  return useIsSmallerThan(767);
};

export const useIsUnderMobileSize = (): boolean => {
  return useIsSmallerThan(301);
};

export const useIsSmallerThan = (windowSize: number): boolean => {
  const size = useWindowSize();
  const isSmallerWindowWidth = useIsSmallerWindowWidth({ size, maxWidth: windowSize });
  return isSmallerWindowWidth;
};
